import { supabase } from '../lib/supabase'
import {
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext
} from './utils'

const TABLE_NAME = 'export_templates'

/**
 * List export templates for the current user (RLS: owner or admin).
 * Admin and Agents only can save/load; RLS enforces.
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: Array<object> | null, error: object | null }>}
 */
export const listExportTemplates = async (options = {}) => {
  const roleContext = await resolveRoleContext({ role: options.role, userId: options.userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const query = supabase
    .from(TABLE_NAME)
    .select('id, name, format, columns, custom_fields, created_at, updated_at')
    .order('updated_at', { ascending: false })

  return handleQuery(query, { fallbackMessage: 'Unable to load export templates.' })
}

/**
 * Get a single export template by id (RLS: owner or admin).
 * @param {string} templateId
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const getExportTemplateById = async (templateId, options = {}) => {
  const roleContext = await resolveRoleContext({ role: options.role, userId: options.userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const query = supabase
    .from(TABLE_NAME)
    .select('*')
    .eq('id', templateId)
    .maybeSingle()

  return handleQuery(query, {
    fallbackMessage: 'Unable to load export template.',
    allowEmpty: false
  })
}

/**
 * Create an export template. RLS allows only admin or agent; created_by must be current user.
 * @param {{ name: string, format: string, columns: string[], custom_fields: Array<{ label: string, value?: string }> }} payload
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const createExportTemplate = async (payload, options = {}) => {
  const roleContext = await resolveRoleContext({ role: options.role, userId: options.userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const role = roleContext.role
  const userId = roleContext.userId

  if (role !== 'admin' && role !== 'agent') {
    return accessDeniedResponse('Only admins and agents can save export templates.')
  }

  if (!userId) {
    return accessDeniedResponse('User id is required to create a template.')
  }

  const format = (payload.format || 'csv').toLowerCase()
  if (!['csv', 'xlsx', 'pdf'].includes(format)) {
    return {
      data: null,
      error: { message: 'Format must be csv, xlsx, or pdf.', code: 'invalid_input' }
    }
  }

  const data = {
    name: payload.name?.trim() || 'Untitled template',
    created_by: userId,
    format,
    columns: Array.isArray(payload.columns) ? payload.columns : [],
    custom_fields: Array.isArray(payload.custom_fields) ? payload.custom_fields : []
  }

  const query = supabase.from(TABLE_NAME).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to save export template.',
    allowEmpty: false
  })
}

/**
 * Update an export template (RLS: owner or admin).
 * @param {string} templateId
 * @param {{ name?: string, format?: string, columns?: string[], custom_fields?: Array<{ label: string, value?: string }> }} updates
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const updateExportTemplate = async (templateId, updates, options = {}) => {
  const roleContext = await resolveRoleContext({ role: options.role, userId: options.userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const payload = { ...updates }
  if (payload.format) {
    payload.format = payload.format.toLowerCase()
    if (!['csv', 'xlsx', 'pdf'].includes(payload.format)) {
      return {
        data: null,
        error: { message: 'Format must be csv, xlsx, or pdf.', code: 'invalid_input' }
      }
    }
  }
  if (payload.columns !== undefined && !Array.isArray(payload.columns)) {
    payload.columns = []
  }
  if (payload.custom_fields !== undefined && !Array.isArray(payload.custom_fields)) {
    payload.custom_fields = []
  }

  const query = supabase
    .from(TABLE_NAME)
    .update(payload)
    .eq('id', templateId)
    .select('*')
    .single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update export template.',
    allowEmpty: false
  })
}

/**
 * Delete an export template (RLS: owner or admin).
 * @param {string} templateId
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>}
 */
export const deleteExportTemplate = async (templateId, options = {}) => {
  const roleContext = await resolveRoleContext({ role: options.role, userId: options.userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const query = supabase.from(TABLE_NAME).delete().eq('id', templateId).select('id').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete export template.',
    allowEmpty: false
  })
}
